import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError, fmtMXN } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/context/ThemeContext";
import { Flame, Moon, Sun, RefreshCcw, Phone, Search, CheckCircle2, Clock, Bike, Package } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "EN PREPARACIÓN", label: "Preparando", icon: Clock },
  { key: "LISTO", label: "Listo", icon: Package },
  { key: "EN CAMINO", label: "En camino", icon: Bike },
  { key: "ENTREGADO", label: "Entregado", icon: CheckCircle2 },
  { key: "PAGADO", label: "Pagado", icon: CheckCircle2 },
];

export default function SeguimientoPedido() {
  const { numero: paramNumero } = useParams();
  const [params] = useSearchParams();
  const { theme, toggle } = useTheme();

  const [numero, setNumero] = useState(paramNumero || "");
  const [telefono, setTelefono] = useState(params.get("tel") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const consultar = async () => {
    if (!numero || !telefono) {
      toast.error("Captura número y teléfono");
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/public/ordenes/${encodeURIComponent(numero)}?telefono=${encodeURIComponent(telefono)}`);
      setData(r.data);
    } catch (e) {
      setData(null);
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Auto-consulta inicial si llegan parámetros en la URL (una sola vez al montar)
  useEffect(() => {
    const tel = params.get("tel");
    if (!paramNumero || !tel) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/public/ordenes/${encodeURIComponent(paramNumero)}?telefono=${encodeURIComponent(tel)}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-refresh cada 20s mientras el pedido esté activo
  useEffect(() => {
    if (!data || data.cobrada || data.estado_publico === "ENTREGADO" || data.estado_publico === "CANCELADO") return;
    const tick = async () => {
      try {
        const r = await api.get(`/public/ordenes/${encodeURIComponent(data.numero)}?telefono=${encodeURIComponent(telefono)}`);
        setData(r.data);
      } catch (e) { /* silent */ }
    };
    const t = setInterval(tick, 20000);
    return () => clearInterval(t);
  }, [data, telefono]);

  const currentStepIndex = data ? STEPS.findIndex((s) => s.key === data.estado_publico) : -1;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/mkitchen-logo.png" alt="M-Kitchen" className="h-10 w-10 rounded-lg bg-black p-0.5 object-contain" />
            <div>
              <div className="font-black">M-Kitchen</div>
              <div className="text-xs text-muted-foreground">Seguimiento de pedido</div>
            </div>
          </div>
          <Button variant="outline" size="icon" onClick={toggle} data-testid="track-theme-btn">
            <Moon className="h-4 w-4 dark:hidden" /><Sun className="h-4 w-4 hidden dark:inline" />
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">¿Cómo va tu pedido?</h1>
          <p className="mt-1 text-sm text-muted-foreground">Captura tu número de orden y el teléfono que usaste.</p>
        </div>

        <Card>
          <CardContent className="p-4 sm:p-6 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Número de orden</Label>
                <Input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="ORD-2026-000001" data-testid="track-numero-input" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="55 1234 5678" data-testid="track-telefono-input" />
              </div>
            </div>
            <Button onClick={consultar} disabled={loading} className="w-full sm:w-auto font-bold" data-testid="track-consultar-btn">
              <Search className="h-4 w-4 mr-2" />{loading ? "Consultando…" : "Consultar"}
            </Button>
          </CardContent>
        </Card>

        {data && (
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Orden</div>
                  <div className="text-2xl font-black text-primary">{data.numero}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={consultar} title="Actualizar" data-testid="track-refresh-btn"><RefreshCcw className="h-4 w-4" /></Button>
              </div>

              <div className="text-sm space-y-1">
                <div><span className="text-muted-foreground">Cliente:</span> <span className="font-semibold">{data.cliente_nombre || "-"}</span></div>
                <div><span className="text-muted-foreground">Sucursal:</span> <span className="font-semibold">{data.sucursal_nombre || "-"}</span></div>
                <div><span className="text-muted-foreground">Tipo:</span> <span className="font-semibold">{data.entrega_tipo === "DOMICILIO" ? "Entrega a domicilio" : "Recoger en sucursal"}</span></div>
                <div><span className="text-muted-foreground">Total:</span> <span className="font-black text-primary">{fmtMXN(data.total)}</span></div>
              </div>

              {/* Stepper */}
              <div>
                <div className="flex items-center justify-between gap-1 sm:gap-2">
                  {STEPS.filter((s) => data.entrega_tipo === "DOMICILIO" || s.key !== "EN CAMINO").map((s, idx, arr) => {
                    const Icon = s.icon;
                    const reached = currentStepIndex >= 0 && STEPS.indexOf(s) <= currentStepIndex;
                    const active = s.key === data.estado_publico;
                    return (
                      <React.Fragment key={s.key}>
                        <div className="flex flex-col items-center text-center min-w-0">
                          <div className={`flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full border-2 transition-colors ${
                            reached ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
                          } ${active ? "ring-2 ring-primary ring-offset-2 ring-offset-background animate-pulse" : ""}`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className={`mt-1 text-[10px] sm:text-xs font-semibold ${reached ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</div>
                        </div>
                        {idx < arr.length - 1 && (
                          <div className={`flex-1 h-0.5 ${reached && STEPS.indexOf(arr[idx + 1]) <= currentStepIndex ? "bg-primary" : "bg-border"}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                <div className="mt-4 text-center">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Estado actual</div>
                  <div className="text-2xl font-black mt-1" data-testid="track-estado">{data.estado_publico}</div>
                  {data.cobrada && <div className="text-xs text-emerald-500 mt-1">Pago confirmado</div>}
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Productos ({data.items_count})</div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {(data.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between text-sm border-b border-border/60 pb-1">
                      <span>{it.cantidad}× {it.nombre}</span>
                    </div>
                  ))}
                </div>
              </div>

              {data.sucursal_telefono && (
                <div className="pt-2 border-t border-border">
                  <a href={`tel:${data.sucursal_telefono}`} className="inline-flex items-center gap-2 text-primary text-sm font-semibold hover:underline" data-testid="track-call-sucursal">
                    <Phone className="h-4 w-4" />Llamar a la sucursal: {data.sucursal_telefono}
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
